import React, {
  useState,
} from "react";


import Button
  from "../../../components/common/Button";

import ErrorMessage
  from "../../../components/common/ErrorMessage";


import {
  createPaymentOrder,
  createPublicPaymentOrder,
  verifyPayment,
  verifyPublicPayment,
} from "../../../api/payments/payments.api";


/*
|--------------------------------------------------------------------------
| RAZORPAY SCRIPT
|--------------------------------------------------------------------------
*/

const RAZORPAY_SCRIPT_URL =
  "https://checkout.razorpay.com/v1/checkout.js";


const loadRazorpayScript = () => {

  return new Promise((resolve) => {

    /*
    |--------------------------------------------------------------------------
    | ALREADY LOADED
    |--------------------------------------------------------------------------
    */

    if (
      typeof window !== "undefined" &&
      window.Razorpay
    ) {

      resolve(true);

      return;
    }


    /*
    |--------------------------------------------------------------------------
    | EXISTING SCRIPT
    |--------------------------------------------------------------------------
    */

    const existingScript =
      document.querySelector(
        `script[src="${RAZORPAY_SCRIPT_URL}"]`
      );


    if (existingScript) {

      existingScript.addEventListener(
        "load",
        () => resolve(true),
        {
          once: true,
        }
      );


      existingScript.addEventListener(
        "error",
        () => resolve(false),
        {
          once: true,
        }
      );


      return;
    }


    /*
    |--------------------------------------------------------------------------
    | CREATE SCRIPT
    |--------------------------------------------------------------------------
    */

    const script =
      document.createElement(
        "script"
      );


    script.src =
      RAZORPAY_SCRIPT_URL;


    script.async =
      true;


    script.onload =
      () => resolve(true);


    script.onerror =
      () => resolve(false);


    document.body.appendChild(
      script
    );

  });
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const PaymentCheckout = ({
  offer,
  publicToken,
  onPaymentSuccess,
  onPaymentFailed,
  onClose,
}) => {

  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | NO OFFER
  |--------------------------------------------------------------------------
  */

  if (!offer) {

    return null;
  }


  /*
  |--------------------------------------------------------------------------
  | ONLY ACCEPTED OFFERS
  |--------------------------------------------------------------------------
  */

  if (
    offer.status !==
    "ACCEPTED"
  ) {

    return null;
  }


  /*
  |--------------------------------------------------------------------------
  | OFFER EXPIRY
  |--------------------------------------------------------------------------
  */

  const validUntil =
    offer.valid_until ||
    offer.validUntil;


  if (
    validUntil &&
    new Date(validUntil) < new Date()
  ) {

    return (

      <div className="payment-checkout">

        <ErrorMessage
          message={
            "This offer has expired and cannot be paid."
          }
        />

      </div>

    );

  }


  /*
  |--------------------------------------------------------------------------
  | ERROR MESSAGE
  |--------------------------------------------------------------------------
  */

  const getErrorMessage =
    (err) => {

      return (

        err
          ?.response
          ?.data
          ?.message ||

        err
          ?.response
          ?.data
          ?.error ||

        err
          ?.response
          ?.data
          ?.errors
          ?.map?.(
            (item) =>
              item?.message ||
              item
          )
          ?.join?.(", ") ||

        err?.message ||

        err
          ?.error
          ?.description ||

        "Unable to process payment. Please try again."

      );

    };


  /*
  |--------------------------------------------------------------------------
  | HANDLE PAYMENT
  |--------------------------------------------------------------------------
  */

  const handlePayment =
    async () => {

      try {

        setLoading(true);

        setError(null);


        /*
        |--------------------------------------------------------------------------
        | PUBLIC TOKEN VALIDATION
        |--------------------------------------------------------------------------
        */

        if (publicToken) {

          if (
            typeof publicToken !==
              "string" ||
            !publicToken.trim()
          ) {

            throw new Error(
              "Invalid public offer token."
            );

          }

        }


        /*
        |--------------------------------------------------------------------------
        | LOAD RAZORPAY
        |--------------------------------------------------------------------------
        */

        const loaded =
          await loadRazorpayScript();


        if (
          !loaded ||
          !window.Razorpay
        ) {

          throw new Error(
            "Unable to load Razorpay Checkout. Please try again."
          );

        }


        /*
        |--------------------------------------------------------------------------
        | CREATE BACKEND PAYMENT ORDER
        |--------------------------------------------------------------------------
        |
        | PUBLIC:
        |
        | POST /api/payments/public/orders
        |
        | Body:
        |
        | {
        |   token,
        |   gateway
        | }
        |
        | AUTHENTICATED:
        |
        | POST /api/payments/orders
        |
        */

        let response;


        if (publicToken) {

          response =
            await createPublicPaymentOrder(
              publicToken.trim(),
              "razorpay"
            );

        } else {

          if (!offer.id) {

            throw new Error(
              "Offer ID is required to create the payment order."
            );

          }


          response =
            await createPaymentOrder(
              offer.id,
              "razorpay"
            );

        }


        /*
        |--------------------------------------------------------------------------
        | RESPONSE
        |--------------------------------------------------------------------------
        */

        const data =
          response?.data;


        if (!data) {

          throw new Error(
            "Payment order information was not returned by the server."
          );

        }


        /*
        |--------------------------------------------------------------------------
        | RAZORPAY DATA
        |--------------------------------------------------------------------------
        */

        const razorpayData =
          data?.razorpay;


        if (!razorpayData) {

          throw new Error(
            "Razorpay payment information is missing."
          );

        }


        /*
        |--------------------------------------------------------------------------
        | KEY ID
        |--------------------------------------------------------------------------
        */

        if (
          !razorpayData.keyId
        ) {

          throw new Error(
            "Razorpay key ID is missing."
          );

        }


        /*
        |--------------------------------------------------------------------------
        | ORDER ID
        |--------------------------------------------------------------------------
        */

        if (
          !razorpayData.orderId
        ) {

          throw new Error(
            "Razorpay order ID is missing."
          );

        }


        /*
        |--------------------------------------------------------------------------
        | AMOUNT
        |--------------------------------------------------------------------------
        */

        if (
          razorpayData.amount ===
            undefined ||
          razorpayData.amount ===
            null ||
          Number(
            razorpayData.amount
          ) <= 0
        ) {

          throw new Error(
            "Invalid Razorpay payment amount."
          );

        }


        /*
        |--------------------------------------------------------------------------
        | CLIENT
        |--------------------------------------------------------------------------
        */

        const client =
          offer.client ||
          offer.customer ||
          null;


        const clientName =
          client?.name ||
          offer.client_name ||
          offer.clientName ||
          "";


        const clientEmail =
          client?.email ||
          offer.client_email ||
          offer.clientEmail ||
          "";


        const clientPhone =
          client?.phone ||
          offer.client_phone ||
          offer.clientPhone ||
          "";


        /*
        |--------------------------------------------------------------------------
        | OFFER NUMBER
        |--------------------------------------------------------------------------
        */

        const offerNumber =
          data?.offer?.offerNumber ||
          data?.offer?.offer_number ||
          offer.offerNumber ||
          offer.offer_number ||
          "Offer";


        /*
        |--------------------------------------------------------------------------
        | RAZORPAY OPTIONS
        |--------------------------------------------------------------------------
        */

        const options = {

          key:
            razorpayData.keyId,


          amount:
            razorpayData.amount,


          currency:
            razorpayData.currency ||
            offer.currency ||
            "INR",


          name:
            "Harsha Enterprises",


          description:
            `Payment for ${offerNumber}`,


          order_id:
            razorpayData.orderId,


          /*
          |--------------------------------------------------------------------------
          | PREFILL
          |--------------------------------------------------------------------------
          */

          prefill: {

            name:
              clientName,

            email:
              clientEmail,

            contact:
              clientPhone,

          },


          /*
          |--------------------------------------------------------------------------
          | NOTES
          |--------------------------------------------------------------------------
          */

          notes: {

            offerId:
              String(
                data?.offer?.id ||
                offer.id ||
                ""
              ),

            offerNumber:
              offerNumber,

          },


          /*
          |--------------------------------------------------------------------------
          | THEME
          |--------------------------------------------------------------------------
          */

          theme: {

            color:
              "#2563eb",

          },


          /*
          |--------------------------------------------------------------------------
          | PAYMENT SUCCESS
          |--------------------------------------------------------------------------
          */

          handler:
            async (
              razorpayResponse
            ) => {

              try {

                setLoading(true);

                setError(null);


                /*
                |--------------------------------------------------------------------------
                | RAZORPAY RESPONSE
                |--------------------------------------------------------------------------
                */

                const razorpayOrderId =
                  razorpayResponse
                    ?.razorpay_order_id;


                const razorpayPaymentId =
                  razorpayResponse
                    ?.razorpay_payment_id;


                const razorpaySignature =
                  razorpayResponse
                    ?.razorpay_signature;


                if (
                  !razorpayOrderId ||
                  !razorpayPaymentId ||
                  !razorpaySignature
                ) {

                  throw new Error(
                    "Invalid Razorpay payment response."
                  );

                }


                /*
                |--------------------------------------------------------------------------
                | VERIFY PAYMENT
                |--------------------------------------------------------------------------
                |
                | PUBLIC:
                |
                | POST /api/payments/public/:token/verify
                |
                | AUTHENTICATED:
                |
                | POST /api/payments/verify
                |
                */

                let verification;


                if (publicToken) {

                  verification =
                    await verifyPublicPayment(
                      publicToken.trim(),
                      {
                        razorpay_order_id:
                          razorpayOrderId,

                        razorpay_payment_id:
                          razorpayPaymentId,

                        razorpay_signature:
                          razorpaySignature,
                      }
                    );

                } else {

                  verification =
                    await verifyPayment({
                      razorpay_order_id:
                        razorpayOrderId,

                      razorpay_payment_id:
                        razorpayPaymentId,

                      razorpay_signature:
                        razorpaySignature,
                    });

                }


                /*
                |--------------------------------------------------------------------------
                | VERIFIED PAYMENT
                |--------------------------------------------------------------------------
                */

                const verifiedPayment =
                  verification?.data;


                if (
                  !verifiedPayment
                ) {

                  throw new Error(
                    "Payment verification response is invalid."
                  );

                }


                /*
                |--------------------------------------------------------------------------
                | SUCCESS CALLBACK
                |--------------------------------------------------------------------------
                */

                if (
                  onPaymentSuccess
                ) {

                  await onPaymentSuccess(
                    verifiedPayment
                  );

                }

              } catch (
                verificationError
              ) {

                console.error(
                  "Payment verification failed:",
                  verificationError
                );


                const message =
                  getErrorMessage(
                    verificationError
                  );


                setError(
                  message
                );


                if (
                  onPaymentFailed
                ) {

                  onPaymentFailed(
                    verificationError
                  );

                }

              } finally {

                setLoading(
                  false
                );

              }

            },


          /*
          |--------------------------------------------------------------------------
          | CHECKOUT CLOSED
          |--------------------------------------------------------------------------
          */

          modal: {

            ondismiss:
              () => {

                setLoading(
                  false
                );


                if (
                  onClose
                ) {

                  onClose();

                }

              },

          },

        };


        /*
        |--------------------------------------------------------------------------
        | RAZORPAY INSTANCE
        |--------------------------------------------------------------------------
        */

        const razorpay =
          new window.Razorpay(
            options
          );


        /*
        |--------------------------------------------------------------------------
        | PAYMENT FAILED
        |--------------------------------------------------------------------------
        */

        razorpay.on(
          "payment.failed",
          (
            paymentResponse
          ) => {

            console.error(
              "Razorpay payment failed:",
              paymentResponse
            );


            const message =
              paymentResponse
                ?.error
                ?.description ||

              paymentResponse
                ?.error
                ?.reason ||

              "Payment failed. Please try again.";


            setError(
              message
            );


            setLoading(
              false
            );


            if (
              onPaymentFailed
            ) {

              onPaymentFailed(
                paymentResponse
              );

            }

          }
        );


        /*
        |--------------------------------------------------------------------------
        | OPEN RAZORPAY
        |--------------------------------------------------------------------------
        */

        razorpay.open();

      } catch (
        err
      ) {

        console.error(
          "Unable to start payment:",
          err
        );


        const message =
          getErrorMessage(
            err
          );


        setError(
          message
        );


        setLoading(
          false
        );


        if (
          onPaymentFailed
        ) {

          onPaymentFailed(
            err
          );

        }

      }

    };


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (

    <div className="payment-checkout">

      {error && (

        <ErrorMessage
          message={
            error
          }
        />

      )}


      <Button
        type="button"
        variant="success"
        loading={loading}
        disabled={loading}
        onClick={
          handlePayment
        }
      >

        {loading
          ? "Processing..."
          : "Pay Now"}

      </Button>

    </div>

  );

};


export default PaymentCheckout;
